import {
  useSurveyStore,
  selectActiveQuestion
} from "views/landlord-surveys-id/store";
import { SidebarQuestions } from "../sidebar-questions";
import { Card } from "shared/components/Card";
import { IconButton, IconButtonSize } from "shared/components";
import {
  AlertCircleIcon,
  Copy01Icon,
  Delete01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Widget } from "./widgets";
import { WidgetConfiguration } from "./widget-configuration";
import { StartSurveySlice } from "views/land-x-token/slices/StartSurvey";
import { EndSurveySlice } from "views/land-x-token/slices/EndSurvey";
import { ReactNode } from "react";
import { cx } from "shared/utils";
import { HugeiconsIcon } from "@hugeicons/react";

const PageBanner = ({ title }: { title: string }) => {
  return (
    <div className="w-full px-4 mb-4 h-[72px] flex gap-3 bg-white items-center border-b border-alpha-100">
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={20}
        className="text-primary-500 shrink-0 self-start mt-4"
      />
      <p className="text-sm font-medium text-content-500">
        {title}
        <br />
        The page is not configurable.
      </p>
    </div>
  );
};

const QuestionEditor = () => {
  const activeQuestion = useSurveyStore(selectActiveQuestion);
  const deleteQuestion = useSurveyStore((state) => state.deleteQuestion);
  const duplicateQuestion = useSurveyStore((state) => state.duplicateQuestion);

  return (
    <main className="bg-background-900 py-12 px-4 w-full">
      <div
        className="mx-auto flex flex-wrap lg:flex-nowrap gap-4 animate-appear max-w-[1000px] w-full"
        key={activeQuestion?.internal_id}
      >
        <Card className="ml-7 min-w-[300px] max-w-[640px] w-full flex flex-col min-h-[320px] p-6 relative">
          <div className="absolute text-xs flex items-center justify-center font-medium text-content-700 text-center h-5 min-w-5 border border-alpha-100 bg-white -left-8 rounded shadow-xs top-0">
            {(activeQuestion?.index || 0) + 1}
          </div>

          <div className="flex-grow w-full">
            <Widget />
          </div>
          <footer className="mt-6 pt-6 border-t border-alpha-40 flex gap-2 justify-end">
            <IconButton
              size={IconButtonSize.SM}
              HugeIcon={Delete01Icon}
              onClick={deleteQuestion}
            />
            <IconButton
              size={IconButtonSize.SM}
              HugeIcon={Copy01Icon}
              onClick={duplicateQuestion}
            />
          </footer>
        </Card>
        <WidgetConfiguration />
      </div>
    </main>
  );
};

const PagePreview = ({
  children,
  banner
}: {
  children: React.ReactNode;
  banner?: ReactNode;
}) => (
  <div className="flex-col w-full">
    {banner}
    <main className="bg-background-900 flex-grow flex items-center justify-center p-4">
      <div
        className={cx(
          // assuming banner is always 72px
          banner
            ? "h-[calc(100vh-72px-84px-48px)]"
            : "h-[calc(100vh-72px-48px)]",
          "max-w-[992px] w-full h-[calc(100vh-72px-48px)] rounded-2xl border border-alpha-100 shadow-lg bg-white overflow-hidden animate-appear"
        )}
      >
        {children}
      </div>
    </main>
  </div>
);

export const QuestionsSlice = () => {
  const activePage = useSurveyStore((state) => state.activePage);

  return (
    <div className="flex animate-appear">
      <SidebarQuestions />

      {activePage === "start" && (
        <PagePreview
          banner={
            <PageBanner title="The start page will always appear at the beginning of the survey." />
          }
        >
          <StartSurveySlice
            mockLandlordInfo={{
              name: "Landlord name",
              address: "Landlord address"
            }}
          />
        </PagePreview>
      )}

      {activePage === "end" && (
        <PagePreview
          banner={
            <PageBanner title="The end page will always appear at the end of the survey." />
          }
        >
          <EndSurveySlice />
        </PagePreview>
      )}

      {activePage === "question" && <QuestionEditor />}
    </div>
  );
};
